import { GridIcon } from "./icons/GridIcon";
import { AccountIcon } from "./icons/AccountIcon";
import { GlobeIcon } from "./icons/GlobeIcon";
import { PhoneIcon } from "./icons/PhoneIcon";
import { MailIcon } from "./icons/MailIcon";
import SectionCard from "./section-card";
import SectionHeader from "./section-header";
import ContactRow from "./contact-row";

const NEUTRAL_500 = "#767c83";

/*
 * The contact website value must not follow the user's primary color. It uses a
 * fixed accent, mirroring how Business keeps a static `websiteTextColor` from
 * its preset; the event value is `#00296B`. Not passed in the payload.
 */
const WEBSITE_TEXT_COLOR = "#00296B";

/*
 * Contact rows render in design order: name -> website -> phone -> email.
 * Website uses the fixed WEBSITE_TEXT_COLOR; all others use the default text
 * color. `websites`, `phones` and `emails` are arrays of { label, value, href }
 * so the live scanned page can preserve custom labels and make each row actionable.
 */
const EventContactCard = ({
  contactName,
  websites,
  phones,
  emails,
  title,
  nameLabel,
  websiteLabel,
  phoneLabel,
  emailLabel,
  titleFont,
  textFont,
}) => {
  const rowGroups = [
    {
      prefix: "name",
      values: contactName ? [{ value: contactName }] : [],
      icon: <AccountIcon size={18} color={NEUTRAL_500} />,
      label: nameLabel,
    },
    {
      prefix: "website",
      values: websites,
      icon: <GlobeIcon size={18} color={NEUTRAL_500} />,
      label: websiteLabel,
      valueColor: WEBSITE_TEXT_COLOR,
    },
    {
      prefix: "phone",
      values: phones,
      icon: <PhoneIcon color={NEUTRAL_500} />,
      label: phoneLabel,
    },
    {
      prefix: "email",
      values: emails,
      icon: <MailIcon color={NEUTRAL_500} />,
      label: emailLabel,
    },
  ];

  const rows = rowGroups.flatMap(
    ({ prefix, values, icon, label, valueColor }) =>
      (values || []).map((item, index) => ({
        key: `${prefix}-${index}`,
        icon,
        label: item.label || label,
        value: item.value,
        href: item.href,
        valueColor,
      })),
  );

  if (rows.length === 0) return null;

  return (
    <SectionCard
      className="flex w-full flex-col items-start justify-center px-[12px] py-[20px] xs:p-[20px]"
      id="contactName"
    >
      <SectionHeader
        icon={<GridIcon size={20} color={NEUTRAL_500} />}
        title={title}
        titleFont={titleFont}
      />
      <div className="mb-[15px] h-px w-full rounded-full bg-event-85" />
      <div className="flex w-full flex-col items-center justify-center">
        {rows.map((row, index) => (
          <ContactRow
            key={row.key}
            icon={row.icon}
            label={row.label}
            value={row.value}
            href={row.href}
            valueColor={row.valueColor}
            textFont={textFont}
            isName={row.key.startsWith("name-")}
            /*
             * The last row drops the divider's bottom margin so only the card's
             * padding sits below it (matches the reference's ~20px), avoiding
             * the doubled gap that pushes the next card too far down.
             */
            dividerClassName={index === rows.length - 1 ? "mt-4" : "my-4"}
          />
        ))}
      </div>
    </SectionCard>
  );
};

export default EventContactCard;
